/* eslint-disable max-lines */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Icon } from "@iconify/react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAuth } from "../contexts/JWTContext";
import styles from "./Menu.module.css";
import { menuTabs } from "../components/Submenu/menuTabs";
import CounterBonus from "../components/CounterBonus";
import CheckinExperience from "../components/CheckinExperience";
import ContactModal from "../components/Modals/ContactModal";
import Logo from "../components/Logo";

const getTabLabel = (tabId) =>
  menuTabs.find((tab) => tab.id === tabId)?.label || "เมนู";

const Toast = ({ message, type = "warning", show, onClose }) => {
  useEffect(() => {
    if (!show) return undefined;
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [show, onClose]);

  if (!show) return null;

  const config = {
    info: { icon: "ℹ️", bg: "#DBEAFE", border: "#3B82F6", text: "#1E40AF" },
    warning: {
      icon: "⚠️",
      bg: "#FEF3C7",
      border: "#F59E0B",
      text: "#92400E",
    },
    error: { icon: "❌", bg: "#FEE2E2", border: "#EF4444", text: "#991B1B" },
  }[type] || {
    icon: "⚠️",
    bg: "#FEF3C7",
    border: "#F59E0B",
    text: "#92400E",
  };

  return (
    <div
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        gap: "12px",
        maxWidth: "400px",
        padding: "16px 20px",
        borderRadius: "12px",
        backgroundColor: config.bg,
        border: `2px solid ${config.border}`,
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
      }}
    >
      <span style={{ fontSize: "20px" }}>{config.icon}</span>
      <span style={{ color: config.text, fontWeight: 500, flex: 1 }}>
        {message}
      </span>
      <button
        type="button"
        onClick={onClose}
        style={{
          background: "none",
          border: "none",
          fontSize: "18px",
          cursor: "pointer",
          color: config.text,
          padding: 0,
          marginLeft: "8px",
        }}
      >
        ×
      </button>
    </div>
  );
};

const Menu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const currentUser = useSelector((state) => state.user.currentUser);
  const playerBalance = useSelector((state) => state.user.playerBalance);
  const playerCash = useSelector((state) => state.user.playerCash);
  const wheelPoint = useSelector((state) => state.user.wheelPoint);

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const rawTabFromQuery = searchParams.get("tab");
  const initialTabFromQuery =
    rawTabFromQuery === "cashier" ? "deposit" : rawTabFromQuery;
  const [activeTab, setActiveTab] = useState(initialTabFromQuery || "");
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "warning",
  });

  const showToast = useCallback((message, type = "warning") => {
    setToast({ show: true, message, type });
  }, []);

  const hideToast = useCallback(() => {
    setToast({ show: false, message: "", type: "warning" });
  }, []);

  useEffect(() => {
    setActiveTab(initialTabFromQuery || "");
  }, [initialTabFromQuery]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleItemClick = (tabId) => {
    if (!isAuthenticated) {
      return;
    }
    if (tabId === "contact") {
      setIsContactModalOpen(true);
      return;
    }
    setActiveTab(tabId);
    navigate(`/menu?tab=${tabId}`);
  };

  const activeTabConfig = menuTabs.find((tab) => tab.id === activeTab);
  const ActiveComp = activeTabConfig?.component;
  const isModalOpen = Boolean(activeTabConfig);

  const closeModal = useCallback(() => {
    setActiveTab("");
    navigate("/menu");
  }, [navigate]);

  useEffect(() => {
    if (!isModalOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [closeModal, isModalOpen]);

  const username =
    currentUser?.username ||
    currentUser?.name ||
    currentUser?.userName ||
    "ผู้ใช้งาน";
  const withdrawConditionDetail = currentUser?.withdrawConditionDetail;
  const promotionDetail = currentUser?.PromotionDetail;
  const promotionStatus = currentUser?.PromotionStatus;
  const isPromoActive = promotionStatus === "ACTIVE";

  const quickMenu = useMemo(
    () => [
      {
        id: "play",
        label: "เล่นเกม",
        icon: "fluent-emoji-flat:video-game",
        accent: "green",
      },
      {
        id: "deposit",
        label: "ฝากเงิน",
        icon: "fluent-color:money-hand-20",
        accent: "red",
      },
      {
        id: "withdraw",
        label: "ถอนเงิน",
        icon: "fluent-color:money-24",
        accent: "gold",
      },
      {
        id: "autoPromo",
        label: "โปรโมชั่น",
        icon: "fluent-color:gift-24",
        accent: "purple",
      },
      {
        id: "coupon",
        label: "เติมโค้ด",
        icon: "fluent-color:gift-card-24",
        accent: "orange",
      },
      {
        id: "refund",
        label: "คืนยอดเสีย",
        icon: "fluent-color:arrow-sync-16",
        accent: "purple",
      },
      {
        id: "cashtocredit",
        label: "เงินสด > เครดิต",
        icon: "fluent-color:wallet-credit-card-16",
        accent: "blue",
      },
      {
        id: "wheelspin",
        label: "กงล้อพารวย",
        icon: "glyphs-poly:target",
        accent: "orange",
      },

      {
        id: "bonus",
        label: "จัดอันดับ",
        icon: "fluent-color:crown-20",
        accent: "gold",
      },
      {
        id: "history",
        label: "ประวัติ",
        icon: "fluent-color:book-information-24",
        accent: "blue",
      },
      {
        id: "refer",
        label: "แนะนำเพื่อน",
        icon: "fluent-color:megaphone-loud-16",
        accent: "green",
      },
      {
        id: "review",
        label: "รีวิว",
        icon: "fluent-color:clipboard-text-edit-20",
        accent: "gold",
      },
      {
        id: "contact",
        label: "ติดต่อ",
        icon: "fluent-color:headset-48",
        accent: "purple",
      },
      {
        id: "account",
        label: "ตั้งค่า",
        icon: "fluent-color:settings-16",
        accent: "red",
      },
    ],
    [],
  );

  // Get current user from singleUsers for CheckinExperience
  const singleUsers = useSelector((state) => state.user.singleUsers);
  const currentUserForCheckin = useMemo(() => {
    if (!singleUsers) return null;
    if (Array.isArray(singleUsers)) {
      return singleUsers[0] || null;
    }
    if (Array.isArray(singleUsers?.data)) {
      return singleUsers.data[0] || null;
    }
    return singleUsers;
  }, [singleUsers]);

  if (!isAuthenticated) return null;

  const handleQuickMenuClick = (itemId) => {
    if (itemId === "play") {
      navigate("/home");
      return;
    }
    handleItemClick(itemId);
  };

  return (
    <div className={styles.page}>
      <CheckinExperience
        currentUser={currentUserForCheckin}
        showToast={showToast}
      />

      <div className={styles.container}>
        {/* ── Hero Section ── */}
        <section className={styles.heroCard}>
          <div className={styles.heroGlow} />

          <div className={styles.heroTop}>
            <div className={styles.brandBlock}>
              <Logo size="large" className={styles.logo} />
            </div>

            <div className={styles.userGreeting}>
              <span className={styles.statusDot} />
              <span className={styles.greetText}>สวัสดี, {username}</span>
            </div>
          </div>

          {/* Balance Card */}
          <div className={styles.balanceCard}>
            <div className={styles.balanceHeader}>
              <Icon icon="fluent-color:wallet-credit-card-16" width={22} />
              <span className={styles.balanceTitle}>เครดิตคงเหลือ</span>
            </div>
            <div className={styles.balanceValue}>
              ฿{" "}
              {Number(playerBalance || 0).toLocaleString("th-TH", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
            <div className={styles.balanceMeta}>
              <div className={`${styles.balanceStatChip} ${styles.cashChip}`}>
                <div className={styles.balanceStatIconWrap}>
                  <Icon
                    icon="solar:wallet-money-bold-duotone"
                    width={18}
                    className={styles.balanceStatIcon}
                  />
                </div>
                <div className={styles.balanceStatText}>
                  <span className={styles.balanceStatLabel}>เงินสด</span>
                  <span className={styles.balanceStatValue}>
                    ฿ {Number(playerCash || 0).toLocaleString("th-TH")}
                  </span>
                </div>
              </div>

              <div
                className={`${styles.balanceStatChip} ${styles.diamondChip}`}
              >
                <div className={styles.balanceStatIconWrap}>
                  <Icon
                    icon="solar:star-bold-duotone"
                    width={18}
                    className={styles.balanceStatIcon}
                  />
                </div>
                <div className={styles.balanceStatText}>
                  <span className={styles.balanceStatLabel}>แต้มเพชร</span>
                  <span className={styles.balanceStatValue}>
                    {Number(wheelPoint || 0).toLocaleString("th-TH")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className={styles.actionRow}>
            <button
              type="button"
              className={`${styles.ctaButton} ${styles.depositButton}`}
              onClick={() => handleItemClick("deposit")}
            >
              <Icon icon="fluent:arrow-download-24-filled" width={22} />
              <span>ฝากเงิน</span>
            </button>
            <button
              type="button"
              className={`${styles.ctaButton} ${styles.playButton}`}
              onClick={() => navigate("/home")}
            >
              <Icon icon="fluent:play-24-filled" width={22} />
              <span>เข้าเกม</span>
            </button>
          </div>

          {(withdrawConditionDetail || promotionDetail) && (
            <div className={styles.noticeStack}>
              {withdrawConditionDetail && (
                <div className={`${styles.noticeBar} ${styles.noticeBarTurn}`}>
                  <div
                    className={`${styles.noticeIconBox} ${styles.noticeIconTurn}`}
                  >
                    <Icon icon="solar:shield-warning-bold" width={22} />
                  </div>
                  <div className={styles.noticeTextWrap}>
                    <div className={styles.noticeTitle}>ติดเงื่อนไขถอน</div>
                    <div className={styles.noticeDesc}>
                      กรุณาทำเทิร์นให้ครบก่อนถอน
                    </div>
                    <div className={styles.noticeMeta}>
                      {withdrawConditionDetail}
                    </div>
                  </div>
                </div>
              )}

              {promotionDetail && (
                <div
                  className={`${styles.noticeBar} ${
                    isPromoActive
                      ? styles.noticeBarPromoActive
                      : styles.noticeBarPromoPending
                  }`}
                >
                  <div
                    className={`${styles.noticeIconBox} ${
                      isPromoActive
                        ? styles.noticeIconPromoActive
                        : styles.noticeIconPromoPending
                    }`}
                  >
                    <Icon
                      icon={
                        isPromoActive
                          ? "solar:gift-bold"
                          : "solar:gift-line-duotone"
                      }
                      width={22}
                    />
                  </div>
                  <div className={styles.noticeTextWrap}>
                    <div className={styles.noticeTitle}>
                      {isPromoActive
                        ? "กำลังใช้โปรโมชั่น"
                        : "เลือกโปรโมชั่นแล้ว"}
                    </div>
                    <div className={styles.noticeDesc}>{promotionDetail}</div>
                    {!isPromoActive && (
                      <div className={styles.noticeMeta}>
                        ฝากเงินเพื่อเปิดใช้งานโปรนี้
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* ── Quick Menu Grid ── */}
        <section className={styles.menuPanel}>
          <div className={styles.menuPanelHeader}>
            <Icon icon="fluent-color:grid-16" width={20} />
          </div>
          <div className={styles.menuGrid}>
            <CounterBonus variant="menuPanel" />
            {quickMenu.map((item) => {
              const mappedTab = menuTabs.find((tab) => tab.id === item.id);
              const icon = mappedTab?.icon || item.icon;

              return (
                <button
                  key={item.id}
                  type="button"
                  className={`${styles.menuItem} ${
                    item.id === activeTab ? styles.menuItemActive : ""
                  }`}
                  onClick={() => handleQuickMenuClick(item.id)}
                >
                  <div
                    className={`${styles.menuIconWrap} ${styles[`accent_${item.accent}`] || ""}`}
                  >
                    <Icon icon={icon} className={styles.menuIcon} />
                  </div>
                  <div className={styles.menuLabel}>{item.label}</div>
                </button>
              );
            })}
          </div>
        </section>
      </div>
      {isModalOpen && (
        <div className={styles.modalOverlay} onMouseDown={closeModal}>
          <div
            className={styles.modalCard}
            onMouseDown={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{getTabLabel(activeTab)}</h3>
              <button
                type="button"
                className={styles.modalClose}
                onClick={closeModal}
                aria-label="ปิด"
              >
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              {ActiveComp && <ActiveComp />}
            </div>
          </div>
        </div>
      )}
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
      />
    </div>
  );
};

export default Menu;
